import { useEffect, useRef } from "react";
import { useLang } from "./LanguageContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE", "TEXTAREA", "INPUT", "SELECT", "OPTION"]);
const hasLetters = (s) => /\p{L}/u.test(s);

/**
 * Traductor automático a nivel de DOM. Solo modifica el TEXTO visible (nodeValue)
 * y el atributo placeholder; nunca agrega/quita/reordena nodos, por lo que es seguro
 * con React. Cachea en memoria + localStorage. El idioma base del contenido es español.
 */
export default function DomTranslator() {
  const { lang } = useLang();
  const origRef = useRef(new WeakMap());   // textNode -> texto original (español)
  const phOrigRef = useRef(new WeakMap()); // element -> placeholder original
  const cacheRef = useRef({});             // { lang: { src: traducido } }
  const langRef = useRef(lang);

  useEffect(() => {
    langRef.current = lang;
    if (!cacheRef.current[lang]) {
      try { cacheRef.current[lang] = JSON.parse(localStorage.getItem("supera_tr_" + lang) || "{}"); }
      catch { cacheRef.current[lang] = {}; }
    }

    let observer = null;
    let pendingNodes = new Map();  // src -> Set(textNode)
    let pendingAttrs = new Map();  // src -> Set(element)
    let flushTimer = null;
    let walkTimer = null;
    let destroyed = false;

    const skip = (el) => {
      let n = el;
      while (n) {
        if (n.nodeType === 1) {
          if (SKIP_TAGS.has(n.tagName)) return true;
          if (n.getAttribute && (n.getAttribute("data-no-i18n") !== null || n.getAttribute("contenteditable") === "true")) return true;
        }
        n = n.parentNode;
      }
      return false;
    };

    const pause = (fn) => {
      if (observer) observer.disconnect();
      try { fn(); } finally {
        if (observer && !destroyed) observer.observe(document.body, { childList: true, subtree: true, characterData: true });
      }
    };

    const scheduleFlush = () => {
      if (flushTimer) clearTimeout(flushTimer);
      flushTimer = setTimeout(flush, 120);
    };
    const scheduleWalk = () => {
      if (walkTimer) clearTimeout(walkTimer);
      walkTimer = setTimeout(walk, 200);
    };

    const applyText = (node, target) => {
      let orig = origRef.current.get(node);
      if (orig == null) { orig = node.nodeValue; origRef.current.set(node, orig); }
      const key = (orig || "").trim();
      if (!key) return;
      if (target === "ES") { if (node.nodeValue !== orig) node.nodeValue = orig; return; }
      const c = cacheRef.current[target] || (cacheRef.current[target] = {});
      if (c[key] != null) {
        const out = orig.replace(key, c[key]);
        if (node.nodeValue !== out) node.nodeValue = out;
      } else {
        if (!pendingNodes.has(key)) pendingNodes.set(key, new Set());
        pendingNodes.get(key).add(node);
        scheduleFlush();
      }
    };

    const applyPlaceholder = (el, target) => {
      let orig = phOrigRef.current.get(el);
      if (orig == null) { orig = el.getAttribute("placeholder") || ""; phOrigRef.current.set(el, orig); }
      const key = (orig || "").trim();
      if (!key || !hasLetters(key)) return;
      if (target === "ES") { if (el.getAttribute("placeholder") !== orig) el.setAttribute("placeholder", orig); return; }
      const c = cacheRef.current[target] || (cacheRef.current[target] = {});
      if (c[key] != null) el.setAttribute("placeholder", orig.replace(key, c[key]));
      else { if (!pendingAttrs.has(key)) pendingAttrs.set(key, new Set()); pendingAttrs.get(key).add(el); scheduleFlush(); }
    };

    const walk = () => {
      if (destroyed) return;
      const target = langRef.current;
      const textNodes = [];
      const tw = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
        acceptNode(n) {
          const v = n.nodeValue;
          if (!v || !v.trim() || !hasLetters(v)) return NodeFilter.FILTER_REJECT;
          if (skip(n.parentNode)) return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_ACCEPT;
        },
      });
      let node;
      while ((node = tw.nextNode())) textNodes.push(node);
      pause(() => {
        textNodes.forEach((n) => applyText(n, target));
        document.querySelectorAll("[placeholder]").forEach((el) => { if (!skip(el)) applyPlaceholder(el, target); });
      });
    };

    const flush = async () => {
      const target = langRef.current;
      if (target === "ES") { pendingNodes.clear(); pendingAttrs.clear(); return; }
      const keys = new Set([...pendingNodes.keys(), ...pendingAttrs.keys()]);
      if (!keys.size) return;
      const items = Array.from(keys);
      const batchNodes = pendingNodes; const batchAttrs = pendingAttrs;
      pendingNodes = new Map(); pendingAttrs = new Map();
      let map = {};
      try {
        const res = await fetch(`${BACKEND}/api/translate`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ target, texts: items }),
        });
        map = (await res.json()).map || {};
      } catch { return; }
      const c = cacheRef.current[target] || (cacheRef.current[target] = {});
      Object.assign(c, map);
      try { localStorage.setItem("supera_tr_" + target, JSON.stringify(c)); } catch {}
      if (langRef.current !== target || destroyed) return;
      pause(() => {
        batchNodes.forEach((set, key) => {
          const trd = map[key]; if (trd == null) return;
          set.forEach((n) => { const orig = origRef.current.get(n) ?? n.nodeValue; n.nodeValue = orig.replace(key, trd); });
        });
        batchAttrs.forEach((set, key) => {
          const trd = map[key]; if (trd == null) return;
          set.forEach((el) => { const orig = phOrigRef.current.get(el) ?? el.getAttribute("placeholder") ?? ""; el.setAttribute("placeholder", orig.replace(key, trd)); });
        });
      });
    };

    // primera pasada
    walk();
    // observador para contenido dinámico / re-render de React
    observer = new MutationObserver(() => scheduleWalk());
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });

    return () => {
      destroyed = true;
      if (observer) observer.disconnect();
      if (flushTimer) clearTimeout(flushTimer);
      if (walkTimer) clearTimeout(walkTimer);
    };
  }, [lang]);

  return null;
}
